import { useTranslate } from 'react-admin';
import {
  Box,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type {
  ReconciliationResolution,
  ShareAdjustmentProposal,
} from 'src/pages/admin/services/vaultService';

import { formatInstant } from '../vaults/helpers';
import { LongId, NUMERIC } from '../records/cells';
import { PROPOSAL_COLUMNS, RECONCILIATION_NS as NS, RESOLUTION_COLUMNS } from './constants';
import { namesBreak } from './helpers';

const EMPTY = '—';

const STATUS_COLOR = {
  AWAITING_APPROVAL: 'warning',
  APPLIED: 'success',
  CANCELLED: 'default',
} as const;

export const breakLabel = (row: {
  breakValuedAt: string | null;
  breakAttemptSeq: number | null;
}) =>
  namesBreak(row) ? `${formatInstant(row.breakValuedAt as string)} #${row.breakAttemptSeq}` : EMPTY;

const Empty = ({ text }: { text: string }) => (
  <Card sx={{ p: 3 }}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {text}
    </Typography>
  </Card>
);

export const ProposalsTable = ({
  rows,
  onOpen,
}: {
  rows: ShareAdjustmentProposal[];
  onOpen: (row: ShareAdjustmentProposal) => void;
}) => {
  const translate = useTranslate();
  if (rows.length === 0) return <Empty text={translate(`${NS}.proposals.empty`)} />;
  return (
    <Card>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 960 }}>
          <TableHead>
            <TableRow>
              {PROPOSAL_COLUMNS.map((c) => (
                <TableCell key={c} sx={{ whiteSpace: 'normal', verticalAlign: 'bottom' }}>
                  {translate(`${NS}.proposals.columns.${c}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={STATUS_COLOR[row.status]}
                    label={translate(`${NS}.status.${row.status}`)}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  {row.holderId ? (
                    <LongId value={row.holderId} head={8} tail={6} />
                  ) : (
                    translate(`${NS}.shareNeutral`)
                  )}
                </TableCell>
                <TableCell sx={NUMERIC}>{row.deltaShares}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {row.reasonCode}
                </TableCell>
                <TableCell sx={NUMERIC}>{breakLabel(row)}</TableCell>
                <TableCell>{row.proposedBy}</TableCell>
                <TableCell sx={NUMERIC}>{formatInstant(row.proposedAt)}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" onClick={() => onOpen(row)}>
                    {translate(`${NS}.proposals.open`)}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};

export const ResolutionsTable = ({ rows }: { rows: ReconciliationResolution[] }) => {
  const translate = useTranslate();
  if (rows.length === 0) return <Empty text={translate(`${NS}.resolutions.empty`)} />;
  return (
    <Card>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {RESOLUTION_COLUMNS.map((c) => (
                <TableCell key={c} sx={{ whiteSpace: 'normal', verticalAlign: 'bottom' }}>
                  {translate(`${NS}.resolutions.columns.${c}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={NUMERIC}>{breakLabel(row)}</TableCell>
                <TableCell sx={{ minWidth: 200, overflowWrap: 'anywhere' }}>{row.cause}</TableCell>
                <TableCell sx={{ minWidth: 160, overflowWrap: 'anywhere' }}>
                  {row.evidenceRef}
                </TableCell>
                <TableCell>{row.approvers.join(', ')}</TableCell>
                <TableCell>
                  <LongId value={row.adjProposalId} head={8} tail={6} />
                </TableCell>
                <TableCell sx={NUMERIC}>{formatInstant(row.resolvedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};
